const MAX_QUANTITY = 2147483647
const id = value => String(value ?? '')
const stock = value => Number.isFinite(Number(value)) ? Math.max(0, Number(value)) : 0

export function australiaSkuLabel(sku = {}) {
  return (sku.dealerItemSkuSpecReqDTOs || []).map(group => group.dealerItemSkuSpecDTO?.specName).filter(Boolean).join(' / ') || sku.title || 'Option'
}

// Older products can omit the flattened specification string/group list. The
// nested real SKU associations remain authoritative, including every dimension.
export function normalizeAustraliaPurchaseDetail(detail = {}) {
  if (detail.hasCombo === true && !Array.isArray(detail.comboList)) throw new Error('Unable to load bundle options. Please try again.')
  const groups = new Map()
  const skus = (detail.dealerItemSkuDTOList || []).map(sku => {
    const pairs = (sku.dealerItemSkuSpecReqDTOs || []).filter(group => group.id != null && group.dealerItemSkuSpecDTO?.id != null)
    for (const group of pairs) {
      const key = id(group.id)
      if (!groups.has(key)) groups.set(key, { id: key, specName: group.specName, dealerItemSkuSpecReqDTOs: [] })
      const values = groups.get(key).dealerItemSkuSpecReqDTOs
      const value = group.dealerItemSkuSpecDTO
      if (!values.some(item => id(item.id) === id(value.id))) values.push({ ...value, id: id(value.id) })
    }
    return { ...sku, id: id(sku.id), spItemSkuSpecificationIds: pairs.length ? pairs.map(group => `${group.id}-${group.dealerItemSkuSpecDTO.id}`).join(',') + ',' : sku.spItemSkuSpecificationIds }
  })
  return { ...detail, id: id(detail.id), dealerShopId: id(detail.dealerShopId), dealerItemSkuDTOList: skus,
    dealerItemSkuSpecDTOs: detail.dealerItemSkuSpecDTOs?.length ? detail.dealerItemSkuSpecDTOs : [...groups.values()] }
}

export function australiaPurchaseCombo(detail, comboCode) {
  if (comboCode === null) return null
  const combo = detail.hasCombo === true && (detail.comboList || []).find(item => id(item.comboCode) === id(comboCode))
  if (!combo || Number(combo.status ?? 1) !== 1) throw new Error('This bundle is no longer available. Please select a purchase option again.')
  return combo
}

export function australiaPurchaseMaximum(detail, comboCode, perSetSkuIds) {
  let combo
  try { combo = australiaPurchaseCombo(detail, comboCode) } catch { return 0 }
  const counts = new Map()
  perSetSkuIds.filter(Boolean).forEach(value => counts.set(id(value), (counts.get(id(value)) || 0) + 1))
  const maxima = [MAX_QUANTITY, ...(combo ? [Math.floor(stock(combo.comboInventory))] : [])]
  for (const [key, count] of counts) {
    const sku = detail.dealerItemSkuDTOList?.find(item => id(item.id) === key)
    maxima.push(sku && Number(sku.state ?? 1) !== 0 ? Math.floor(stock(sku.inventory) / count) : 0)
  }
  return Math.min(...maxima)
}

export function buildAustraliaPurchaseLine(detail, comboCode, perSetSkuIds, quantity) {
  if (!Number.isInteger(quantity) || quantity < 1 || quantity > MAX_QUANTITY) throw new Error('Enter a valid quantity.')
  const combo = australiaPurchaseCombo(detail, comboCode)
  const count = combo ? Number(combo.packageQty) : 1
  if (!Number.isInteger(count) || count < 1 || perSetSkuIds.length !== count || perSetSkuIds.some(value => value == null || value === '')) throw new Error('Please select all product options.')
  const totals = new Map()
  for (const value of perSetSkuIds) {
    const key = id(value)
    const sku = detail.dealerItemSkuDTOList?.find(item => id(item.id) === key)
    if (!sku || Number(sku.state ?? 1) === 0) throw new Error('A selected option is no longer available. Please select it again.')
    const total = (totals.get(key) || 0) + quantity
    if (total > MAX_QUANTITY || total > stock(sku.inventory)) throw new Error(`Not enough stock for ${australiaSkuLabel(sku)}. Please change your selection or quantity.`)
    totals.set(key, total)
  }
  if (quantity > australiaPurchaseMaximum(detail, comboCode, perSetSkuIds)) throw new Error('Not enough stock for this bundle. Please reduce the quantity.')
  const dealerShopId = id(detail.dealerShopId)
  if (!dealerShopId) throw new Error('Unable to determine the shop. Please refresh the product.')
  if (!combo) {
    const dealerItemSkuId = id(perSetSkuIds[0])
    return { itemType: 1, dealerShopId, dealerItemSkuId, buyAmount: quantity, specsInfo: australiaSkuLabel(detail.dealerItemSkuDTOList.find(sku => id(sku.id) === dealerItemSkuId)) }
  }
  return { itemType: 2, dealerShopId, comboCode: id(combo.comboCode), comboBuyQty: quantity,
    selectedSkuList: [...totals].map(([dealerItemSkuId, buyAmount]) => ({ dealerItemSkuId, buyAmount })) }
}

export function australiaSpuDetailLink(item = {}) {
  const combo = Number(item.itemType) === 2 && item.comboCode
  const spuId = combo ? item.dealerItemSpuId || item.spItemSpuId : item.id || item.spuId || item.dealerItemSpuId || item.spItemSpuId
  const query = new URLSearchParams()
  if (spuId) query.set('spuId', id(spuId))
  if (item.dealerShopId) query.set('dealerShopId', id(item.dealerShopId))
  if (combo) query.set('comboCode', id(item.comboCode))
  return query.size ? `/product?${query}` : item.link || '#'
}

export function australiaGuestPurchaseItem(detail, line, price) {
  const combo = line.itemType === 2 ? australiaPurchaseCombo(detail, line.comboCode) : null
  const selected = combo ? line.selectedSkuList : [{ dealerItemSkuId: line.dealerItemSkuId, buyAmount: line.buyAmount }]
  const selectedSkuList = selected.map(item => {
    const sku = detail.dealerItemSkuDTOList.find(sku => id(sku.id) === item.dealerItemSkuId)
    return { ...item, skuTitle: australiaSkuLabel(sku), specsInfo: australiaSkuLabel(sku), inventory: sku.inventory }
  })
  const sku = detail.dealerItemSkuDTOList.find(sku => id(sku.id) === line.dealerItemSkuId)
  return { ...line, id: id(detail.id), dealerItemSpuId: id(detail.id), spItemSpuId: id(detail.id), title: detail.title,
    price, inventory: sku?.inventory, iconUrl: sku?.iconUrl || combo?.coverImage || detail.iconUrl, payWay: detail.payWay, sendTypes: detail.sendTypes,
    ...(combo ? { cartLineKey: `LOCAL_${combo.comboCode}_${Date.now()}_${Math.random().toString(36).slice(2)}`, comboName: `${detail.title} - ${combo.comboName}`, comboPackageQty: Number(combo.packageQty), comboUnitPrice: price, comboInventory: australiaPurchaseMaximum(detail, line.comboCode, selectedSkuList.flatMap(item => Array(item.buyAmount / line.comboBuyQty).fill(item.dealerItemSkuId))), comboAmount: price * line.comboBuyQty, selectedSkuList } : {}) }
}
